/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Operations › Caps & limits, rebuilt on the kit (overhaul plan step 3c, "Caps & limits"). The same calls as before
 * (GET /v1/admin/usage-caps, PUT /v1/admin/config {budget} and {limits}, PUT /v1/admin/usage-caps) with the same
 * forms and the same save per panel, so a save in one panel never touches what is typed in another. What changed is
 * how it reads: the per-depth limits sit in one grid so Fast to Ultra line up across rows, the long explanations are
 * panel footers, money and tokens read in the console's one format, and a refusal is a callout in the service's words.
 *
 * The cap fields keep the text as typed: a value that is not a cap (-4, 1,500, letters) is refused under its field and
 * nothing is sent, where it used to be saved as 0 or left out, clearing the cap (#232, #233); a blank field still clears
 * one. Every cap save carries the caps its form was built from, so a save made from caps another admin has changed since
 * is refused (409) and the panel shows the caps in force.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { Check, Info, Pencil, RotateCcw, Trash2, Users } from 'lucide-react';
import { api, type BudgetSettings, type UserCaps, type UserSpendRow, type UsageCapsStatus, type UsageLimitRow } from '../lib/api';
import {
  GLOBAL_CAP_FIELDS, USER_CAP_FIELDS, budgetToForm, capsChangedElsewhere, capsSet, formToBudgetPatch, formToLimitsPatch, formToUserCaps, formatLimitValue, globalCapHint, limitFields,
  limitsToForm, resetLimitsPatch, userCapsToForm,
  type GlobalCapErrors, type GlobalCapForm, type LimitForm, type UserCapErrors, type UserCapForm,
} from '../lib/usageCaps';
import {
  Button, Callout, EmptyState, Field, Grid, Icon, Input, NumberInput, PageHeader, Panel, Skeleton, Stack, StatusChip, Table, Tag,
  type Column,
} from '../ui';
import { money, tokens } from '../ui/format';

/** The six per-person cap fields, three to a row; a refused field says why under it. */
function CapInputs({ value, errors, onChange, disabled }: { value: UserCapForm; errors: UserCapErrors; onChange: (key: keyof UserCaps, text: string) => void; disabled?: boolean }) {
  return (
    <Grid cols={3}>
      {USER_CAP_FIELDS.map((f) => (
        <Field key={f.key} label={f.label} error={errors[f.key]}>
          {/* Text, not number: a number input reports '--4', '1,500' or '1e' as blank, which would read as "no cap". */}
          <NumberInput
            placeholder="no cap"
            value={value[f.key]}
            disabled={disabled}
            onChange={(e) => onChange(f.key, e.target.value)}
          />
        </Field>
      ))}
    </Grid>
  );
}

/** The errors without the one for `key` (the person is changing that field). */
function without<K extends string>(errors: Partial<Record<K, string>>, key: K): Partial<Record<K, string>> {
  if (!(key in errors)) return errors;
  return Object.fromEntries(Object.entries(errors).filter(([name]) => name !== key)) as Partial<Record<K, string>>;
}

/** Move to the first refused field of a form once the refusal has rendered, so a screen reader lands on it and reads why. */
function focusFirstRefused(form: RefObject<HTMLDivElement | null>): void {
  requestAnimationFrame(() => form.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus());
}

/** A row's spend in one window: cost and tokens, in the console's one format. */
const usage = (usd: number, tok: number) => `${money(usd)} · ${tokens(tok)}`;

export function UsageCaps() {
  const [data, setData] = useState<UsageCapsStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [global, setGlobal] = useState<GlobalCapForm>(budgetToForm(undefined));
  // What each cap form was last built from, sent with its save so the service can refuse it (409) when another admin has
  // changed those caps since. Not the copy another panel's save refreshes, which would let this one overwrite that change.
  const [globalBase, setGlobalBase] = useState<BudgetSettings | undefined>(undefined);
  const [globalErrors, setGlobalErrors] = useState<GlobalCapErrors>({});
  const [def, setDef] = useState<UserCapForm>(userCapsToForm(undefined));
  const [defBase, setDefBase] = useState<UserCaps>({});
  const [defErrors, setDefErrors] = useState<UserCapErrors>({});
  const [email, setEmail] = useState('');
  const [userForm, setUserForm] = useState<UserCapForm>(userCapsToForm(undefined));
  // The override Edit loaded into the form: the address it belongs to and its caps (null: the person had none).
  const [userBase, setUserBase] = useState<{ email: string; caps: UserCaps | null } | null>(null);
  const [userErrors, setUserErrors] = useState<UserCapErrors>({});
  const [saving, setSaving] = useState<'global' | 'default' | 'user' | 'limits' | ''>('');
  const [globalSaved, setGlobalSaved] = useState<'' | 'saved' | 'unchanged'>('');
  // One removal at a time: a second click would carry the same loaded caps and be refused as stale.
  const removing = useRef(false);
  const globalFields = useRef<HTMLDivElement>(null);
  const defaultFields = useRef<HTMLDivElement>(null);
  const userFields = useRef<HTMLDivElement>(null);
  const [limitForm, setLimitForm] = useState<LimitForm>({});
  // The rows the limits form was last built from: a save is the difference from these, not from a copy refreshed by
  // another panel's save (which would undo a change someone else made in between).
  const [limitBase, setLimitBase] = useState<UsageCapsStatus['limits']>([]);
  const [limitErrors, setLimitErrors] = useState<string[]>([]);
  const [limitInvalid, setLimitInvalid] = useState<string[]>([]);
  const [limitsSaved, setLimitsSaved] = useState<'' | 'saved' | 'unchanged'>('');
  const limitsPanel = useRef<HTMLDivElement>(null);

  // Re-read everything, but reset only the form of the panel that was saved (all of them on first load): a save in one
  // panel never wipes what is typed, and not yet saved, in another.
  const refresh = async (reset: { def?: boolean; global?: boolean; limits?: boolean }): Promise<UsageCapsStatus> => {
    const d = await api.getUsageCaps();
    setData(d);
    if (reset.global) {
      setGlobal(budgetToForm(d.budget));
      setGlobalBase(d.budget);
      setGlobalErrors({});
    }
    if (reset.limits) {
      setLimitBase(d.limits);
      setLimitForm(limitsToForm(d.limits, d.depthNames));
    }
    if (reset.def) {
      setDef(userCapsToForm(d.default));
      setDefBase(d.default);
      setDefErrors({});
    }
    return d;
  };
  const load = () => refresh({ def: true, global: true, limits: true }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  useEffect(() => { void load(); }, []);

  // The four global caps live in the shared settings document (`budget`), so they save through the config route;
  // the service merges the patch, so the per-user caps and the other budget fields are untouched. Only the caps that
  // changed are sent, each with the value the form was built from, which the service checks before it writes.
  const saveGlobal = async () => {
    if (!data) return;
    setError(null);
    setGlobalSaved('');
    const { patch, expected, errors } = formToBudgetPatch(global, globalBase, data.budgetDefaults);
    setGlobalErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstRefused(globalFields);
      return;
    }
    // Nothing changed: nothing is sent, and the panel says so rather than 'saved'.
    if (!Object.keys(patch).length) {
      setGlobalSaved('unchanged');
      return;
    }
    setSaving('global');
    try {
      await api.saveConfig({ budget: patch, expectedGlobalCaps: expected });
      await refresh({ global: true });
      setGlobalSaved('saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      // Another admin changed one of these caps after the form was loaded: nothing was saved; show the caps in force.
      if (capsChangedElsewhere(e, 'global_caps_changed')) await refresh({ global: true }).catch(() => undefined);
    } finally {
      setSaving('');
    }
  };

  // The answer and usage limits live in the shared settings (`limits`) and save through the config route; only what
  // changed is sent, and the service checks the bounds again and names any limit it refuses.
  const saveLimits = async (patch: Record<string, unknown>) => {
    setSaving('limits');
    setError(null);
    setLimitsSaved('');
    try {
      // Nothing changed: nothing is sent, and the panel says so rather than 'saved'.
      if (!Object.keys(patch).length) {
        setLimitsSaved('unchanged');
        return;
      }
      await api.saveConfig({ limits: patch });
      await refresh({ limits: true });
      setLimitsSaved('saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving('');
    }
  };
  const submitLimits = () => {
    if (!data) return;
    const { patch, errors, invalid } = formToLimitsPatch(limitBase, limitForm, data.depthNames);
    setLimitErrors(errors);
    setLimitInvalid(invalid);
    if (!errors.length) {
      void saveLimits(patch);
      return;
    }
    setLimitsSaved('');
    // Move to the first refused field, so a screen reader lands on it and reads its error.
    requestAnimationFrame(() => limitsPanel.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus());
  };

  // The per-user caps save through PUT /v1/admin/usage-caps, each with what its form was built from (expectedDefault,
  // setUser.expected): the service writes nothing when another admin has changed those caps since (409).
  const saveDefault = async () => {
    if (!data) return;
    setError(null);
    const { caps, errors } = formToUserCaps(def);
    setDefErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstRefused(defaultFields);
      return;
    }
    setSaving('default');
    try {
      const d = await api.setUsageCaps({ userCapsDefault: caps, expectedDefault: defBase });
      setData(d);
      setDef(userCapsToForm(d.default));
      setDefBase(d.default);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      if (capsChangedElsewhere(e, 'user_caps_changed')) await refresh({ def: true }).catch(() => undefined);
    } finally {
      setSaving('');
    }
  };

  const saveUser = async () => {
    const address = email.trim();
    if (!address || !data) return;
    setError(null);
    const { caps, errors } = formToUserCaps(userForm);
    setUserErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstRefused(userFields);
      return;
    }
    const key = address.toLowerCase();
    // What this save replaces: the override Edit loaded for this address, else the one the table shows for it (none: null).
    const expected = userBase?.email === key ? userBase.caps : Object.hasOwn(data.perUser, key) ? data.perUser[key]! : null;
    setSaving('user');
    try {
      const d = await api.setUsageCaps({ setUser: { email: address, caps, expected } });
      setData(d);
      setEmail('');
      setUserForm(userCapsToForm(undefined));
      setUserBase(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      if (capsChangedElsewhere(e, 'user_caps_changed')) {
        // Show this person's caps in force, so the change is made again from them.
        try {
          const d = await refresh({});
          const current = Object.hasOwn(d.perUser, key) ? d.perUser[key]! : null;
          setUserForm(userCapsToForm(current));
          setUserBase({ email: key, caps: current });
        } catch {
          // The callout already says nothing was saved.
        }
      }
    } finally {
      setSaving('');
    }
  };

  const editUser = (row: { email: string; caps: UserCaps }) => {
    setEmail(row.email);
    setUserForm(userCapsToForm(row.caps));
    setUserBase({ email: row.email.toLowerCase(), caps: row.caps });
    setUserErrors({});
  };

  const removeUser = async (row: { email: string; caps: UserCaps }) => {
    if (removing.current) return;
    removing.current = true;
    setError(null);
    try {
      setData(await api.setUsageCaps({ setUser: { email: row.email, caps: null, expected: row.caps } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed');
      if (capsChangedElsewhere(e, 'user_caps_changed')) await refresh({}).catch(() => undefined);
    } finally {
      removing.current = false;
    }
  };

  const perUser: Array<{ email: string; caps: UserCaps }> = data ? Object.entries(data.perUser).map(([em, caps]) => ({ email: em, caps })) : [];
  const setCount = capsSet(data?.budget).length;
  const changedLimits = data ? data.limits.filter((row) => row.overridden).length : 0;
  const loading = data === null && error === null;
  // The depths a per-depth limit is set by, in the service's order, so every per-depth row has the same columns.
  const depths = data ? Object.keys(data.depthNames) : [];

  const userColumns: Array<Column<{ email: string; caps: UserCaps }>> = [
    { key: 'user', header: 'User', mono: true, cell: (row) => row.email },
    { key: 'caps', header: 'Caps', muted: true, cell: (row) => USER_CAP_FIELDS.filter((f) => row.caps[f.key] != null).map((f) => `${f.label}: ${row.caps[f.key]}`).join(' · ') || '—' },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end', width: '96px',
      cell: (row) => (
        <span className="ui-row-actions">
          <Button variant="ghost" size="sm" icon={Pencil} aria-label={`Edit ${row.email}`} title="Edit" onClick={() => editUser(row)} />
          <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove ${row.email}`} title="Remove" onClick={() => void removeUser(row)} />
        </span>
      ),
    },
  ];

  const spendColumns: Array<Column<UserSpendRow>> = [
    { key: 'user', header: 'User', mono: true, cell: (row) => row.email },
    { key: 'day', header: 'Today', numeric: true, cell: (row) => usage(row.dayUsd, row.dayTokens) },
    { key: 'week', header: '7 days', numeric: true, hideOnPhone: true, cell: (row) => usage(row.weekUsd, row.weekTokens) },
    { key: 'month', header: '30 days', numeric: true, cell: (row) => usage(row.monthUsd, row.monthTokens) },
  ];

  const limitColumns: Array<Column<UsageLimitRow>> = [
    {
      key: 'limit', header: 'Limit', width: '32%',
      cell: (l) => (
        <div className="ui-cell-stack">
          <span className="ui-cell-title">
            <strong>{l.name}</strong>
            {l.overridden && <StatusChip tone="warn">changed</StatusChip>}
          </span>
          <span className="ui-cell-sub"><Tag>{l.key}</Tag> · {l.source}</span>
        </div>
      ),
    },
    {
      key: 'value', header: 'Value',
      cell: (l) => {
        const fields = limitFields(l, data?.depthNames ?? {});
        const perDepth = fields.some((f) => f.level);
        return (
          <div className="ui-cell-stack">
            <div className={perDepth ? 'ui-limit-grid' : 'ui-limit-one'} data-depths={perDepth ? depths.length : undefined}>
              {fields.map((f) => (
                <Field key={f.id} label={f.label} labelHidden={!f.level} className="ui-limit-field">
                  {/* Text, not number: a browser reports '100,000' typed in a number input as blank. */}
                  <NumberInput
                    type="text"
                    size="sm"
                    inputMode="numeric"
                    aria-label={f.level ? `${l.name}, ${f.label}` : l.name}
                    invalid={limitInvalid.includes(f.id)}
                    aria-describedby={`limit-hint-${l.setting}${limitInvalid.includes(f.id) ? ' limit-errors' : ''}`}
                    placeholder={String(f.release)}
                    value={limitForm[f.id] ?? ''}
                    onChange={(e) => { setLimitsSaved(''); setLimitForm({ ...limitForm, [f.id]: e.target.value }); }}
                  />
                </Field>
              ))}
            </div>
            <span className="ui-cell-sub" id={`limit-hint-${l.setting}`}>
              {l.unit} · release value {formatLimitValue(l.default, data?.depthNames)} · {l.bounds.min.toLocaleString('en-US')}–{l.bounds.max.toLocaleString('en-US')}
            </span>
          </div>
        );
      },
    },
    { key: 'meaning', header: 'What it does', width: '26%', muted: true, cell: (l) => l.meaning },
  ];

  return (
    <>
      <PageHeader
        meta={data ? (
          <>
            <StatusChip tone={setCount ? 'ok' : 'neutral'}>{setCount} of {GLOBAL_CAP_FIELDS.length} fleet caps set</StatusChip>
            <StatusChip tone={changedLimits ? 'warn' : 'neutral'}>{changedLimits ? `${changedLimits} limit${changedLimits === 1 ? '' : 's'} changed` : 'Release limit values'}</StatusChip>
          </>
        ) : loading ? <Skeleton width="180px" /> : null}
      />
      <div className="ui-page">
        <Stack>
          {error && <Callout tone="bad" role="alert" title="The service refused">{error}</Callout>}

          <Panel
            title="Global caps"
            description="Every app, every user: the whole fleet's spend and each person's request rate"
            actions={data && <StatusChip tone={setCount ? 'ok' : 'neutral'}>{setCount} of {GLOBAL_CAP_FIELDS.length} set</StatusChip>}
            footer={(
              <>
                <Icon icon={Info} size="sm" />
                <span>
                  Spend caps are checked before every turn against the total across every app, cache and web-search cost included, plus the worst case of
                  the answers in progress: at 80% a <code>usage.threshold</code> webhook event fires; at 100% Gyro pauses everywhere, telling the person which
                  cap was reached, until the window moves on. Over the rate limit a request is refused (429) and the user is asked to wait a moment. Saved to <code>budget</code> in
                  the shared settings, the same place Settings › Model &amp; key › Budget guardrails writes.
                </span>
              </>
            )}
          >
            {!data ? (
              <Grid cols={2}>
                {GLOBAL_CAP_FIELDS.map((f) => (
                  <Field key={f.key} label={f.label}><Skeleton size="lg" /></Field>
                ))}
              </Grid>
            ) : (
              <div className="ui-form" ref={globalFields}>
                <Grid cols={2}>
                  {GLOBAL_CAP_FIELDS.map((f) => (
                    <Field key={f.key} label={f.label} help={globalCapHint(f.key, data.budgetDefaults)} error={globalErrors[f.key]}>
                      {/* Text, not number: a number input reports '--4', '1,500' or '1e' as blank, which would read as "no cap". */}
                      <NumberInput
                        placeholder={f.key === 'ratePerMin' ? `${data.budgetDefaults.ratePerMin} (built in)` : f.key === 'burst' ? `${data.budgetDefaults.burst} (built in)` : 'no cap'}
                        value={global[f.key]}
                        onChange={(e) => { setGlobalSaved(''); setGlobal({ ...global, [f.key]: e.target.value }); setGlobalErrors(without(globalErrors, f.key)); }}
                      />
                    </Field>
                  ))}
                </Grid>
                <div className="ui-actions">
                  <Button variant="primary" icon={Check} busy={saving === 'global'} onClick={() => void saveGlobal()}>Save global caps</Button>
                  {globalSaved === 'saved' && <StatusChip tone="ok" role="status">saved</StatusChip>}
                  {globalSaved === 'unchanged' && <StatusChip tone="neutral" role="status">nothing changed</StatusChip>}
                </div>
              </div>
            )}
          </Panel>

          <Panel
            title="Default per-user cap"
            description="Applies to every user who has no override of their own"
            footer={(
              <>
                <Icon icon={Info} size="sm" />
                <span>When a user reaches a cap, Gyro pauses for them until the window resets; other users and the global caps are not affected. Leave a field blank for “no cap” on that window. A per-user override below fully replaces this default for that user.</span>
              </>
            )}
          >
            <div className="ui-form" ref={defaultFields}>
              <CapInputs value={def} errors={defErrors} onChange={(key, text) => { setDef({ ...def, [key]: text }); setDefErrors(without(defErrors, key)); }} disabled={!data} />
              <div className="ui-actions">
                <Button variant="primary" icon={Check} busy={saving === 'default'} disabled={!data} onClick={() => void saveDefault()}>Save default cap</Button>
              </div>
            </div>
          </Panel>

          <Panel
            title="Per-user override"
            description="One person's own caps, in place of the default"
            actions={perUser.length > 0 ? <StatusChip tone="info">{perUser.length} override{perUser.length === 1 ? '' : 's'}</StatusChip> : null}
            flush
          >
            <div className="ui-panel-pad">
              <div className="ui-form" ref={userFields}>
                <Field label="User email">
                  <Input type="email" placeholder="alice@exante360.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <CapInputs value={userForm} errors={userErrors} onChange={(key, text) => { setUserForm({ ...userForm, [key]: text }); setUserErrors(without(userErrors, key)); }} disabled={!data} />
                <div className="ui-actions">
                  <Button variant="primary" icon={Check} busy={saving === 'user'} disabled={!email.trim() || !data} onClick={() => void saveUser()}>Save user cap</Button>
                </div>
              </div>
            </div>
            {perUser.length > 0 && (
              <Table
                label="Per-user overrides"
                columns={userColumns}
                rows={perUser}
                rowKey={(row) => row.email}
                stickyHeader={false}
                phone={{
                  title: (row) => row.email,
                  meta: (row) => USER_CAP_FIELDS.filter((f) => row.caps[f.key] != null).map((f) => `${f.label}: ${row.caps[f.key]}`).join(' · ') || '—',
                  value: (row) => (
                    <span className="ui-row-actions">
                      <Button variant="ghost" size="sm" icon={Pencil} aria-label={`Edit ${row.email}`} onClick={() => editUser(row)} />
                      <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove ${row.email}`} onClick={() => void removeUser(row)} />
                    </span>
                  ),
                }}
              />
            )}
          </Panel>

          <Panel
            flush
            title="Current usage"
            description="Rolling windows: today, the last 7 days and the last 30 days"
            footer={<><Icon icon={Info} size="sm" /><span>Each cell is cost · tokens.</span></>}
          >
            <Table
              label="Current usage"
              columns={spendColumns}
              rows={data?.spend ?? []}
              rowKey={(row) => row.email}
              loading={loading}
              skeletonRows={3}
              empty={<EmptyState compact icon={Users} title="No usage recorded yet">Each person's spend appears here once they start asking Gyro.</EmptyState>}
              phone={{
                title: (row) => row.email,
                meta: (row) => `Today ${usage(row.dayUsd, row.dayTokens)}`,
                value: (row) => usage(row.monthUsd, row.monthTokens),
                sub: () => '30 days',
              }}
            />
          </Panel>

          <div ref={limitsPanel}>
            <Panel
              flush
              title="Answer and usage limits"
              description="What one question or one person may do, on top of the caps above"
              actions={data && <StatusChip tone={changedLimits ? 'warn' : 'neutral'}>{changedLimits ? `${changedLimits} changed` : 'release values'}</StatusChip>}
              footer={(
                <>
                  <Icon icon={Info} size="sm" />
                  <span>
                    Each starts at its release value; a saved change applies from the next question in the chat, the desktop app and the Office add-in. A blank
                    field, or the release value, puts it back. An answer cut off by one of these limits says which one; a question refused for too many at
                    once says so in the browser and in Office (the desktop app shows a general error until its next update). Saved to <code>limits</code> in
                    the shared settings, and every change is audited. Teams keeps its own limit of 2 questions at once, the Hub routes have none, and an app
                    on the Claude Agent SDK engine keeps its own tool rounds (GYRO_CLAUDE_MAX_TURNS), caps a call at 64,000 tokens and runs at most 2 tasks
                    per person.
                  </span>
                </>
              )}
            >
              <Table stacked
                label="Answer and usage limits"
                columns={limitColumns}
                rows={data?.limits ?? []}
                rowKey={(l) => l.key}
                loading={loading}
                skeletonRows={4}
                stickyHeader={false}
                className="ui-table--tall"
              />
              {data && (
                <div className="ui-panel-pad ui-panel-pad--top">
                  {limitErrors.length > 0 && (
                    <Callout tone="bad" title="Not saved">
                      <ul id="limit-errors" role="alert" className="ui-list">
                        {limitErrors.map((message) => <li key={message}>{message}</li>)}
                      </ul>
                    </Callout>
                  )}
                  <div className="ui-actions">
                    <Button variant="primary" icon={Check} busy={saving === 'limits'} onClick={submitLimits}>Save limits</Button>
                    <Button icon={RotateCcw} disabled={saving === 'limits' || !changedLimits} onClick={() => { setLimitErrors([]); setLimitInvalid([]); void saveLimits(resetLimitsPatch(data.limits)); }}>
                      Reset to release values
                    </Button>
                    {limitsSaved === 'saved' && <StatusChip tone="ok" role="status">saved</StatusChip>}
                    {limitsSaved === 'unchanged' && <StatusChip tone="neutral" role="status">nothing changed</StatusChip>}
                  </div>
                </div>
              )}
            </Panel>
          </div>
        </Stack>
      </div>
    </>
  );
}
